<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import BillingAPI from 'dashboard/api/billing';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ButtonV4 from 'next/button/Button.vue';

const props = defineProps({
  manualDetails: { type: Object, default: () => ({}) },
  providers: { type: Array, default: () => [] },
});

const emit = defineEmits(['submitted']);

const { t } = useI18n();

const dialogRef = ref(null);
const selection = ref(null);
const externalRef = ref('');
const screenshot = ref(null);
const isSubmitting = ref(false);
const isRedirecting = ref(false);

const khaltiEnabled = computed(() => props.providers.includes('khalti'));
const manualEnabled = computed(() => props.providers.includes('manual_qr'));

const description = computed(() =>
  manualEnabled.value
    ? t('BILLING_SETTINGS.MANUAL_PAYMENT.DESCRIPTION')
    : t('BILLING_SETTINGS.KHALTI.DESCRIPTION')
);

const formattedAmount = computed(() =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'NPR',
    maximumFractionDigits: 0,
  }).format(selection.value?.amount || 0)
);

const canSubmit = computed(() => !!screenshot.value && !isSubmitting.value);

const open = payload => {
  selection.value = payload;
  externalRef.value = '';
  screenshot.value = null;
  isRedirecting.value = false;
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const onFileChange = event => {
  const [file] = event.target.files || [];
  screenshot.value = file || null;
};

const submit = async () => {
  if (!canSubmit.value) return;

  isSubmitting.value = true;
  try {
    await BillingAPI.createSubmission({
      kind: selection.value.kind,
      planId: selection.value.planId,
      credits: selection.value.credits,
      provider: 'manual_qr',
      externalRef: externalRef.value,
      screenshot: screenshot.value,
    });
    close();
    useAlert(t('BILLING_SETTINGS.MANUAL_PAYMENT.SUCCESS'));
    emit('submitted');
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('BILLING_SETTINGS.MANUAL_PAYMENT.ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};

// Khalti owns the rest of the flow from here: the customer pays in Khalti's
// checkout and comes back through /khalti/callback, so `isRedirecting` is
// deliberately left true on the happy path — this page is going away.
const payWithKhalti = async () => {
  if (isRedirecting.value) return;

  isRedirecting.value = true;
  try {
    const { data } = await BillingAPI.createSubmission({
      kind: selection.value.kind,
      planId: selection.value.planId,
      credits: selection.value.credits,
      provider: 'khalti',
    });
    window.location.assign(data.payment_url);
  } catch (error) {
    isRedirecting.value = false;
    useAlert(
      error?.response?.data?.error || t('BILLING_SETTINGS.KHALTI.ERROR')
    );
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      $t('BILLING_SETTINGS.MANUAL_PAYMENT.TITLE', { name: selection?.name })
    "
    :description="description"
    width="lg"
    :show-confirm-button="false"
    :show-cancel-button="false"
  >
    <div class="flex flex-col gap-5">
      <div
        v-if="khaltiEnabled"
        class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1"
      >
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('BILLING_SETTINGS.KHALTI.TITLE') }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{ $t('BILLING_SETTINGS.KHALTI.DESCRIPTION') }}
          </span>
        </div>
        <span class="text-lg font-medium text-n-slate-12">
          {{ formattedAmount }}
        </span>
        <ButtonV4
          sm
          solid
          blue
          :is-loading="isRedirecting"
          :disabled="isRedirecting"
          @click="payWithKhalti"
        >
          {{ $t('BILLING_SETTINGS.KHALTI.BUTTON') }}
        </ButtonV4>
      </div>

      <div
        v-if="khaltiEnabled && manualEnabled"
        class="text-xs font-medium tracking-wide uppercase text-n-slate-10"
      >
        {{ $t('BILLING_SETTINGS.MANUAL_PAYMENT.OR_PAY_MANUALLY') }}
      </div>

      <template v-if="manualEnabled">
        <div class="flex flex-col items-center gap-3">
          <img
            v-if="manualDetails.qr_image_url"
            :src="manualDetails.qr_image_url"
            alt=""
            class="w-48 h-48 border rounded-xl border-n-weak"
          />
          <span class="text-lg font-medium text-n-slate-12">
            {{ $t('BILLING_SETTINGS.MANUAL_PAYMENT.AMOUNT') }}:
            {{ formattedAmount }}
          </span>
        </div>

        <div
          class="grid gap-2 p-4 text-sm rounded-xl bg-n-solid-2 text-n-slate-11"
        >
          <div
            v-if="manualDetails.account_name"
            class="flex justify-between gap-2"
          >
            <span>{{
              $t('BILLING_SETTINGS.MANUAL_PAYMENT.ACCOUNT_NAME')
            }}</span>
            <span class="font-medium text-n-slate-12">
              {{ manualDetails.account_name }}
            </span>
          </div>
          <div
            v-if="manualDetails.account_number"
            class="flex justify-between gap-2"
          >
            <span>{{
              $t('BILLING_SETTINGS.MANUAL_PAYMENT.ACCOUNT_NUMBER')
            }}</span>
            <span class="font-medium text-n-slate-12">
              {{ manualDetails.account_number }}
            </span>
          </div>
          <div
            v-if="manualDetails.bank_name"
            class="flex justify-between gap-2"
          >
            <span>{{ $t('BILLING_SETTINGS.MANUAL_PAYMENT.BANK_NAME') }}</span>
            <span class="font-medium text-n-slate-12">
              {{ manualDetails.bank_name }}
            </span>
          </div>
          <p
            v-if="manualDetails.instructions"
            class="pt-2 border-t border-n-weak"
          >
            {{ manualDetails.instructions }}
          </p>
        </div>

        <Input
          v-model="externalRef"
          :label="$t('BILLING_SETTINGS.MANUAL_PAYMENT.TRANSACTION_ID')"
          :placeholder="
            $t('BILLING_SETTINGS.MANUAL_PAYMENT.TRANSACTION_ID_PLACEHOLDER')
          "
        />

        <div class="flex flex-col gap-1">
          <label
            class="text-sm font-medium text-n-slate-12"
            for="payment-screenshot"
          >
            {{ $t('BILLING_SETTINGS.MANUAL_PAYMENT.SCREENSHOT') }}
          </label>
          <input
            id="payment-screenshot"
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            class="text-sm text-n-slate-11"
            @change="onFileChange"
          />
          <span class="text-xs text-n-slate-10">
            {{ $t('BILLING_SETTINGS.MANUAL_PAYMENT.SCREENSHOT_HINT') }}
          </span>
        </div>

        <div class="flex justify-end gap-2">
          <ButtonV4 sm faded slate @click="close">
            {{ $t('BILLING_SETTINGS.MANUAL_PAYMENT.CANCEL') }}
          </ButtonV4>
          <ButtonV4
            sm
            solid
            blue
            :is-loading="isSubmitting"
            :disabled="!canSubmit"
            @click="submit"
          >
            {{ $t('BILLING_SETTINGS.MANUAL_PAYMENT.SUBMIT') }}
          </ButtonV4>
        </div>
      </template>
    </div>
  </Dialog>
</template>
